import { useState, useEffect } from "react";
import { FileText, Shield, CheckCircle2, IndianRupee } from "lucide-react";
import { apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CouponInput, CurrencySelector } from "./CouponCurrency";

const OrderReportModal = ({ open, onClose, token, user, activeModule }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedModule, setSelectedModule] = useState(activeModule || "relationship");
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [currency, setCurrency] = useState("INR");

  useEffect(() => {
    if (open) {
      setSelectedModule(activeModule || "relationship");
      setCouponCode("");
      setCouponData(null);
      setCurrency("INR");
      apiCall("GET", "/report-orders/config", null, token).then(r => {
        if (r.success) setConfig(r.data);
      });
    }
  }, [open, token, activeModule]);

  const handleOrder = async () => {
    setLoading(true);
    const res = await apiCall("POST", "/report-orders/create", { module: selectedModule, coupon_code: couponCode, currency }, token);
    if (!res.success) {
      toast.error(res.error || "Failed to create order");
      setLoading(false);
      return;
    }

    const { razorpay_order_id, razorpay_key_id, order_id } = res.data;

    // Free order (100% coupon) — skip Razorpay, auto-verify
    if (res.data.total <= 0) {
      const verifyRes = await apiCall("POST", "/report-orders/verify", { order_id, razorpay_payment_id: "coupon_free" }, token);
      setLoading(false);
      if (verifyRes.success) {
        toast.success("Report ordered (free with coupon)! Draft generated and coach notified.");
        onClose(true);
      } else { toast.error(verifyRes.error || "Verification failed"); onClose(false); }
      return;
    }

    if (!razorpay_order_id || !razorpay_key_id) {
      toast.error("Payment gateway not configured");
      setLoading(false);
      return;
    }

    const options = {
      key: razorpay_key_id,
      amount: res.data.total * 100,
      currency: currency,
      name: "SIWON HARMONY",
      description: `3A Framework Report - ${selectedModule === "life" ? "Life Wisdom Coaching" : "Relationship Wisdom Coaching"}`,
      order_id: razorpay_order_id,
      prefill: {
        name: user?.name || "",
        email: user?.email || "",
      },
      theme: { color: "#B8860B" },
      handler: async (response) => {
        const verifyRes = await apiCall("POST", "/report-orders/verify", {
          order_id,
          razorpay_payment_id: response.razorpay_payment_id,
        }, token);
        setLoading(false);
        if (verifyRes.success) {
          toast.success("Report ordered! A draft has been generated and your coach will finalize it shortly.");
          onClose(true);
        } else {
          toast.error(verifyRes.error || "Payment verification failed");
          onClose(false);
        }
      },
      modal: {
        ondismiss: () => { setLoading(false); },
      },
    };

    if (window.Razorpay) {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } else {
      toast.error("Payment gateway loading... please try again.");
      setLoading(false);
    }
  };

  if (!config) return null;

  // Pick correct base price per selected currency
  const basePrice = currency === "USD" ? (config.price_usd ?? config.price) : (config.price_inr ?? config.price);
  // GST logic: INR always; USD attracts IGST UNLESS admin has an active LUT (zero-rated export)
  const gstPct = currency === "USD" ? (config.lut_active ? 0 : config.gst_percent) : config.gst_percent;
  const discount = couponData ? (couponData.discount_type === "percentage" ? Math.round(basePrice * couponData.discount_value / 100) : couponData.discount_value) : 0;
  const finalPrice = Math.max(basePrice - discount, 0);
  const gstAmount = Math.round(finalPrice * (gstPct / 100));
  const totalAmount = finalPrice + gstAmount;
  const moduleLabel = selectedModule === "life" ? "Life Wisdom Coaching" : "Relationship Wisdom Coaching";
  const sym = currency === "USD" ? "$" : "\u20B9";
  const gstLabel = currency === "USD" ? "IGST" : "GST";

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-cosmic-deep border border-divine-gold/20 max-w-lg max-h-[90vh] overflow-y-auto mt-4" data-testid="order-report-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-divine-gold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Order 3A Framework Report
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Description */}
          <p className="text-chakra-crown/70 text-sm leading-relaxed">
            {config.description}
          </p>

          {/* Module Selection */}
          <div>
            <p className="text-chakra-crown/50 text-xs font-heading tracking-wider mb-2">SELECT MODULE</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "relationship", label: "Relationship", icon: "heart" },
                { id: "life", label: "Life Wisdom Coaching", icon: "compass" },
              ].map(m => (
                <button
                  key={m.id}
                  onClick={() => setSelectedModule(m.id)}
                  data-testid={`order-module-${m.id}`}
                  className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                    selectedModule === m.id
                      ? "border-divine-gold bg-divine-gold/10 text-divine-gold"
                      : "border-divine-gold/10 text-chakra-crown/50 hover:border-divine-gold/30"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* What's Included */}
          <div className="p-4 rounded-xl bg-cosmic-light/5 border border-divine-gold/10">
            <p className="text-divine-gold text-xs font-heading tracking-wider mb-3">WHAT YOU GET</p>
            {[
              "Auto-generated report from your assessment data",
              "Scriptural references & 3A framework insights",
              "Coach review, refinement & personalization",
              "Downloadable PDF report",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2 mb-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                <span className="text-chakra-crown/70 text-xs">{item}</span>
              </div>
            ))}
          </div>

          {/* Currency & Coupon */}
          <div className="grid grid-cols-2 gap-4">
            <CurrencySelector value={currency} onChange={(c) => { setCurrency(c); setCouponCode(""); setCouponData(null); }} />
            <CouponInput
              token={token}
              module={selectedModule}
              currency={currency}
              orderType="report"
              onApply={(code, data) => { setCouponCode(code); setCouponData(data); }}
              onClear={() => { setCouponCode(""); setCouponData(null); }}
            />
          </div>

          {/* Pricing */}
          <div className="p-4 rounded-xl bg-divine-gold/5 border border-divine-gold/20">
            <div className="flex justify-between text-sm text-chakra-crown/70 mb-1">
              <span>Report ({moduleLabel})</span>
              <span>{sym}{basePrice}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-emerald-400 mb-1">
                <span>Discount ({couponCode})</span>
                <span>-{sym}{discount}</span>
              </div>
            )}
            {gstPct > 0 && (
              <div className="flex justify-between text-sm text-chakra-crown/50 mb-2">
                <span>{gstLabel} ({gstPct}%)</span>
                <span>{sym}{gstAmount}</span>
              </div>
            )}
            {currency === "USD" && config.lut_active && (
              <div className="flex justify-between text-xs text-emerald-400/80 mb-2">
                <span>Export under LUT — IGST zero-rated</span>
                <span>—</span>
              </div>
            )}
            <div className="border-t border-divine-gold/20 pt-2 flex justify-between text-base font-bold text-divine-gold">
              <span>Total</span>
              <span>{sym}{totalAmount}</span>
            </div>
          </div>

          {/* Pay Button */}
          <Button
            onClick={handleOrder}
            disabled={loading}
            data-testid="order-report-pay-btn"
            className="w-full bg-divine-gold hover:bg-divine-gold/90 text-cosmic-void font-heading text-base py-6"
          >
            {loading ? "Processing..." : `Pay ${currency} ${totalAmount} & Order Report`}
          </Button>

          <div className="flex items-center justify-center gap-1.5 text-chakra-crown/40 text-[10px]">
            <Shield className="w-3 h-3" />
            Secured by Razorpay
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderReportModal;
